import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import useWindowSize from '@hooks/use-window-size';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { BrandsInterface, ProductDraftRequestPayload, CatalogInterface } from '@interfaces/models/deposit';

export type BrandSearchProps = {
  universes: CatalogInterface[];
  isBanned: boolean;
  isJewelry: boolean;
  setIsBanned: Dispatch<SetStateAction<boolean>>;
  listingDraftPayload: ProductDraftRequestPayload;
  setListingDraftAttribute: (data: Partial<ProductDraftRequestPayload>) => void;
  brandsByFilter: BrandsInterface[];
  brandSearchTerm: string;
  setBrandSearchTerm: Dispatch<SetStateAction<string>>;
};

export const BRAND_MAX_LENGTH = 60;
export const UNSIGNED_BRAND_ID = '2837';
export const JEWELRY_CATEGORY_ID = 'icr00015';

export const useBrandSearchPresenter = ({
  isJewelry,
  isBanned,
  setIsBanned,
  listingDraftPayload,
  setListingDraftAttribute,
  brandsByFilter,
  brandSearchTerm,
  setBrandSearchTerm,
}: BrandSearchProps) => {
  const { isWiderThanMd, isWiderThanLg, isWiderThanXl } = useWindowSize();
  const { sendAnalyticEvent } = useAnalyticEvents('listingForm');
  const [optionsListIsOpen, setOptionsListIsOpen] = useState<boolean>(false);
  const initialInputBrandsPos = useRef<number>(0);
  const [keyboardSelectIndex, setKeyBoardSelectIndex] = useState<number>(0);
  const [brandSelectOrigin, setBrandSelectOrigin] = useState<string>('');
  const optionsElement = useRef<HTMLUListElement>(null);
  const inputBrands = useRef<HTMLInputElement>(null);

  const brandLengthError = brandSearchTerm.length > BRAND_MAX_LENGTH;
  const brandHasError =
    (brandSearchTerm && !listingDraftPayload.brand && brandSelectOrigin && !optionsListIsOpen) ||
    isBanned ||
    brandLengthError;
  const isJewelryUnsigned = isJewelry && brandsByFilter?.length === 0;

  const getIsNoMatchDisplayedForNonJewelry = (brand: BrandsInterface) => {
    const isMatchedWithOption = brand?.name?.toLocaleLowerCase().includes(brandSearchTerm.toLocaleLowerCase());
    return !isJewelry && brandsByFilter.length === 1 && brand.id === UNSIGNED_BRAND_ID && !isMatchedWithOption;
  };

  useEffect(() => {
    if (!listingDraftPayload.brand) {
      setIsBanned(false);
    }
  }, [listingDraftPayload.brand]);

  const handleCloseOptionsList = () => {
    const classList: DOMTokenList = optionsElement.current?.classList;
    classList?.add('vc-d-none');
    if (classList?.contains('vc-d-none')) {
      setOptionsListIsOpen(false);
    }
  };

  const handleChangeBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    optionsElement.current?.classList.remove('vc-d-none');
    setBrandSearchTerm(e.target.value);
    setBrandSelectOrigin('text');
    setListingDraftAttribute({ brand: '' });
    setOptionsListIsOpen(true);
  };

  const handleSelectBrand = (brand: BrandsInterface) => {
    if (!brand || isJewelryUnsigned) {
      return;
    }
    setBrandSearchTerm(brand.name);
    setListingDraftAttribute({ brand: brand.id });
    setBrandSelectOrigin('list');
    handleCloseOptionsList();
    setIsBanned(brand.banned);
    inputBrands.current?.blur();
  };

  const handleFocusBrand = () => {
    if (brandsByFilter) {
      optionsElement.current?.classList.remove('vc-d-none');
    }
    if (!isWiderThanMd) {
      const brandsPanelHeight = 200 + 4; // 200px height + 4px of margin
      const positionToScroll =
        initialInputBrandsPos.current > 0 ? initialInputBrandsPos.current - brandsPanelHeight : 0;
      if (!(isWiderThanLg && !isWiderThanXl) && positionToScroll > 0) {
        if (typeof window !== 'undefined') {
          window.scroll(0, positionToScroll);
        }
      }
    }
  };

  const handleKeyboardSelect = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!brandsByFilter) {
      return;
    }
    switch (e.key) {
      case 'Enter':
        handleSelectBrand(brandsByFilter[keyboardSelectIndex]);
        return;
      case 'ArrowDown':
        setKeyBoardSelectIndex((prev) => (prev + 1) % brandsByFilter.length);
        return;
      case 'ArrowUp':
        setKeyBoardSelectIndex((prev) => (prev - 1 + brandsByFilter.length) % brandsByFilter.length);
        return;
    }
  };

  useEffect(() => {
    if (listingDraftPayload.brand && brandSelectOrigin && document.activeElement !== inputBrands.current) {
      sendAnalyticEvent('submit_brand_list', {
        eventLabel: `brands_${brandSelectOrigin}`,
        eventProperty: brandSearchTerm,
      });
    }
  }, [brandSelectOrigin, listingDraftPayload.brand]);

  // Add an event listener for dismissing the options list
  useEffect(() => {
    const handleDismissOptions = (e) => {
      if (
        optionsElement.current &&
        !optionsElement.current.contains(e.target as Node) &&
        e.target !== inputBrands.current
      ) {
        handleCloseOptionsList();
      }
    };
    document.addEventListener('click', handleDismissOptions, true);
    return () => document.removeEventListener('click', handleDismissOptions, true);
  }, []);

  useEffect(() => {
    if (inputBrands && inputBrands.current?.parentElement) {
      initialInputBrandsPos.current = inputBrands.current?.parentElement?.offsetTop;
    }
  });

  return {
    handleChangeBrand,
    handleSelectBrand,
    handleFocusBrand,
    handleKeyboardSelect,
    inputBrands,
    brandHasError,
    isJewelryUnsigned,
    keyboardSelectIndex,
    optionsElement,
    isJewelry,
    brandLengthError,
    getIsNoMatchDisplayedForNonJewelry,
  };
};
